import {
  styled,
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography
} from '@mui/material';

import Dialog from 'components/shared/Dialog';

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '100%',
    maxWidth: theme.breakpoints.values.large,
    minHeight: theme.spacing(90)
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(4),
    background: theme.palette.grey[50]
  }
}));

export const DialogBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  minHeight: theme.spacing(70)
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.h6.fontSize,
  fontWeight: 700,
  letterSpacing: 0.2
}));

export const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5)
}));

export const SectionPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

export const DetailsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(${theme.spacing(40)}, 0.92fr) minmax(0, 1.08fr)`,
  gap: theme.spacing(2, 3),
  [theme.breakpoints.down('expanded')]: {
    gridTemplateColumns: '1fr'
  }
}));

export const DetailsColumn = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.5)
}));

export const DetailRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${theme.spacing(28)} 1fr`,
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': {
    borderBottom: 0,
    paddingBottom: 0
  },
  '&:first-of-type': {
    paddingTop: 0
  }
}));

export const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: theme.typography.body2.fontSize,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));

export const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary
}));

export const ReasonSelect = styled(Select)(({ theme }) => ({
  minWidth: theme.spacing(27.5),
  backgroundColor: theme.palette.common.white,
  '& .MuiSelect-select': {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25)
  }
}));

export const ReasonOption = styled(MenuItem)(() => ({}));

export const ProposalGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(2),
  [theme.breakpoints.down('expanded')]: {
    gridTemplateColumns: '1fr'
  }
}));

export const SplitLayout = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(${theme.spacing(40)}, 0.92fr) minmax(0, 1.08fr)`,
  gap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.down('expanded')]: {
    gridTemplateColumns: '1fr'
  }
}));

export const SplitColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  minWidth: 0
}));

export const ProposalColumn = styled(Box)(({ theme }) => ({
  minHeight: theme.spacing(27.5),
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

export const ProposalColumnHeader = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  color: theme.palette.primary.main,
  fontSize: theme.typography.h6.fontSize,
  fontWeight: 700,
  backgroundColor: theme.palette.grey[50]
}));

export const CountedHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 2.5),
  backgroundColor: theme.palette.grey[50]
}));

export const CounterBadge = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 700,
  minWidth: theme.spacing(3),
  textAlign: 'right'
}));

export const ProposalEmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.palette.text.secondary,
  minHeight: theme.spacing(15),
  display: 'flex',
  alignItems: 'center'
}));

export const ProposalScrollArea = styled(Box)(({ theme }) => ({
  maxHeight: theme.spacing(27.5),
  overflowY: 'auto'
}));

export const ProposalRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${theme.spacing(18)} ${theme.spacing(
    12
  )} ${theme.spacing(8)} minmax(0, 1fr) auto`,
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 120ms ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  '&:last-of-type': {
    borderBottom: 0
  },
  [theme.breakpoints.down('medium')]: {
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'start'
  }
}));

export const ProposalDate = styled(Typography)(({ theme }) => ({
  whiteSpace: 'nowrap',
  color: theme.palette.text.secondary,
  fontWeight: 600
}));

export const ProposalContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: 0,
  flexWrap: 'wrap'
}));

export const ProposalCode = styled(Typography)(() => ({
  flexShrink: 0,
  fontWeight: 700,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

export const ProposalHours = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  fontWeight: 700,
  color: theme.palette.primary.main,
  whiteSpace: 'nowrap'
}));

export const ProposalPeriods = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  minWidth: 0
}));

export const ProposalPeriod = styled(Typography)(() => ({
  whiteSpace: 'nowrap'
}));

export const ProposalActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5)
}));

export const WorkAgreementList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5)
}));

export const WorkAgreementCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: theme.spacing(2),
  alignItems: 'start',
  padding: theme.spacing(2),
  border: 'none',
  backgroundColor: 'transparent',
  transition: 'none'
}));

export const WorkAgreementCheckbox = styled(Checkbox)(({ theme }) => ({
  padding: 0,
  marginTop: theme.spacing(0.25),
  color: theme.palette.primary.main,
  pointerEvents: 'none'
}));

export const WorkAgreementPrimary = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary
}));

export const WorkAgreementSecondary = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize
}));

export const WorkAgreementHours = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  whiteSpace: 'nowrap'
}));

export const WorkAgreementSettings = styled(Box)(({ theme }) => ({
  gridColumn: '1 / -1',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`
}));

export const WorkAgreementSettingsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75)
}));

export const WorkAgreementSettingsLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: 600
}));

export const WeekdaySelector = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1)
}));

export const WeekdayButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  borderRadius: '50%',
  fontSize: theme.typography.body2.fontSize,
  border: `1px solid ${
    selected ? theme.palette.primary.main : theme.palette.divider
  }`,
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  fontWeight: 700,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.primary.main + '12'
  }
}));

export const Footer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  marginTop: 'auto',
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`
}));

export const LoadingState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  minHeight: theme.spacing(52.5)
}));

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: theme.spacing(27.5)
}));

export const Spinner = styled(CircularProgress)(() => ({}));
